import { Link, useLocation } from "wouter";
import { useState, useEffect, useMemo } from "react";
import { TrendingUp, Phone, Mail, MapPin, Facebook, Instagram, MessageCircle, Star, Search, ShoppingCart, User, LogOut, Package, UserCircle, ChevronDown, Home } from "lucide-react";
import { createMouseEnterHandler, prefetchForRoute } from "@/lib/prefetch-queries";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import type { Quotations } from "@shared/schema";
import { MobileNav } from "./mobile-nav";
import { SearchBar } from "./search-bar";
import logoMonochrome from "@/assets/universo-oro-logo-monochrome-optimized.png";
import logoType2Color from "@/assets/universo-oro-logotype-2color-optimized.png";
import logoType2Color1x from "@/assets/universo-oro-logotype-2color-205.png";
import { useSettings } from "@/hooks/use-cms";
import { normalizePhone } from "@/lib/phone-utils";
import { useCustomerAuth } from "@/contexts/customer-auth-context";
import { useLocalCart } from "@/contexts/local-cart-context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { QuotationStatusIndicator } from "./quotation-status-banner";
import { parsePublicMenuConfig, PUBLIC_MENU_SETTING_KEY, isPublicMenuItemActive } from "./menu-config";

type RawMetalPrice = "gold24k" | "silver999";

export function getLiveRawPrice(
  quotations: Quotations | undefined,
  metal: RawMetalPrice,
): number | undefined {
  const status = quotations?.status;
  if (!status || status.source !== "live" || status.isStale || status.isFallback) {
    return undefined;
  }

  const price = quotations.rawMarketPrices?.[metal];
  return typeof price === "number" && Number.isFinite(price) && price > 0
    ? price
    : undefined;
}

function getCmsSetting(settings: Record<string, any> | undefined, key: string): string | undefined {
  const value = settings?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function getCmsSocialUrl(settings: Record<string, any> | undefined, key: string): string | undefined {
  const value = getCmsSetting(settings, key);
  if (!value) return undefined;

  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:" ? value : undefined;
  } catch {
    return undefined;
  }
}

export function Header() {
  const [location, setLocation] = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Customer authentication state
  const { customer, isAuthenticated, logout } = useCustomerAuth();
  
  // Local cart for guests
  const { totalItems: localCartItems } = useLocalCart();
  
  // Fetch CMS settings for header
  const { data: settings } = useSettings([
    'whatsapp_general',
    'email_contact',
    'phone_tollfree',
    'instagram',
    'facebook',
    PUBLIC_MENU_SETTING_KEY,
  ]);
  
  // Fetch cart for authenticated customers
  const { data: cartData } = useQuery<{ items: any[]; totalItems: number; totalAmount: number }>({
    queryKey: ["/api/cart"],
    enabled: isAuthenticated,
  });
  
  // Show local cart count for guests, server cart count for authenticated users
  const cartItemCount = isAuthenticated ? (cartData?.totalItems || 0) : localCartItems;
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const { data: quotations } = useQuery<Quotations>({
    queryKey: ["/api/quotations"],
  });

  // Only publish raw market quotations when the feed explicitly reports live, valid data.
  const goldPrice = getLiveRawPrice(quotations, "gold24k");
  const silverPrice = getLiveRawPrice(quotations, "silver999");
  const facebookUrl = getCmsSocialUrl(settings, "facebook");
  const instagramUrl = getCmsSocialUrl(settings, "instagram");
  const whatsappNumber = getCmsSetting(settings, "whatsapp_general");
  const whatsappDigits = whatsappNumber ? normalizePhone(whatsappNumber).whatsapp : "";
  const whatsappUrl = whatsappDigits.length >= 8 && whatsappDigits.length <= 15
    ? `https://wa.me/${whatsappDigits}`
    : undefined;
  const contactPhone = getCmsSetting(settings, "phone_tollfree");
  const normalizedContactPhone = contactPhone ? normalizePhone(contactPhone) : undefined;
  const validContactPhone = normalizedContactPhone
    && normalizedContactPhone.whatsapp.length >= 8
    && normalizedContactPhone.whatsapp.length <= 15
    ? normalizedContactPhone
    : undefined;
  const contactEmail = getCmsSetting(settings, "email_contact");
  const validContactEmail = contactEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)
    ? contactEmail
    : undefined;

  const menuItems = parsePublicMenuConfig(settings?.[PUBLIC_MENU_SETTING_KEY]);
  
  const prefetchHandlers = useMemo(() => ({
    carrello: createMouseEnterHandler('carrello'),
    areaClienti: createMouseEnterHandler('areaClienti'),
  }), []);

  const isMenuItemActive = (item: typeof menuItems[0]) => {
    return isPublicMenuItemActive(item, location);
  };

  return (
    <header className="w-full bg-[hsl(var(--header-bg))]">
      {/* Top Bar - Quotazioni e Info */}
      <div className="bg-[hsl(var(--footer-bg))] text-white hidden md:block">
        <div className="container mx-auto max-w-7xl px-4 h-10">
          <div className="flex items-center justify-between h-full text-xs">
            {/* Social Icons */}
            <div className="flex items-center gap-4">
              {facebookUrl && (
                <a
                  href={facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  aria-label="Facebook"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              )}
              {instagramUrl && (
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  aria-label="Instagram"
                >
                  <Instagram className="h-4 w-4" />
                </a>
              )}
              {whatsappUrl && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  aria-label="WhatsApp"
                >
                  <MessageCircle className="h-4 w-4" />
                </a>
              )}
            </div>

            {/* Center Info */}
            <div className="flex items-center gap-6 font-medium">
              {contactPhone && validContactPhone && (
                <a href={`tel:${validContactPhone.tel}`} className="hidden lg:flex items-center gap-2">
                  <Phone className="h-3 w-3" />
                  {contactPhone}
                </a>
              )}
              {validContactEmail && (
                <a href={`mailto:${validContactEmail}`} className="hidden xl:flex items-center gap-2">
                  <Mail className="h-3 w-3" />
                  {validContactEmail}
                </a>
              )}
            </div>

            {/* Live Quotations */}
            <div className="flex items-center gap-4">
              <QuotationStatusIndicator status={quotations?.status} testIdSuffix="header" />
              {/* Raw market prices are omitted until valid live data is available. */}
              {goldPrice !== undefined && <div className="flex items-center gap-2">
                <span className="text-xs opacity-90">ORO 24K:</span>
                <span className="text-uo-gold font-bold tabular-nums inline-block min-w-[4.5rem] text-right">
                  {goldPrice.toFixed(2)}€/g
                </span>
                <TrendingUp className="h-3 w-3 text-green-400" />
              </div>}
              {silverPrice !== undefined && <div className="hidden lg:flex items-center gap-2">
                <span className="text-xs opacity-90">ARGENTO:</span>
                <span className="text-uo-gold font-bold tabular-nums inline-block min-w-[4.5rem] text-right">
                  {silverPrice.toFixed(3)}€/g
                </span>
                <TrendingUp className="h-3 w-3 text-green-400" />
              </div>}
            </div>
          </div>
        </div>
      </div>

      {/* Logo Section - Logo left, Actions right (hidden on mobile and on scroll) */}
      <div className={`bg-white transition-all duration-300 hidden md:block ${
        isScrolled ? 'md:h-0 md:overflow-hidden md:opacity-0' : ''
      }`}>
        <div className="container mx-auto max-w-7xl px-4 py-5">
          <div className="flex justify-between items-center py-4">
            {/* Logo - Left */}
            <Link href="/" data-testid="link-home">
              <div className="cursor-pointer group">
                <img 
                  src={logoType2Color1x} 
                  srcSet={`${logoType2Color1x} 1x, ${logoType2Color} 2x`}
                  alt="UNIVERSO ORO Logo" 
                  className="h-[105px] w-auto"
                  width={205}
                  height={105}
                />
              </div>
            </Link>

            {/* Actions - Right */}
            <div className="flex items-center gap-4">
              {/* Search Bar */}
              <div className="w-64">
                <SearchBar />
              </div>
              
              {/* Shopping Cart */}
              <Link 
                href="/carrello" 
                data-testid="link-cart"
                aria-label="Carrello"
                onMouseEnter={prefetchHandlers.carrello.onMouseEnter}
                onMouseLeave={prefetchHandlers.carrello.onMouseLeave}
              >
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="relative border-uo-gold/30 text-uo-dark hover:bg-uo-gold/10"
                  data-testid="button-cart"
                  aria-label="Carrello"
                >
                  <ShoppingCart className="h-5 w-5" />
                  {cartItemCount > 0 && (
                    <Badge 
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-uo-gold text-uo-dark"
                      data-testid="cart-count"
                    >
                      {cartItemCount > 99 ? '99+' : cartItemCount}
                    </Badge>
                  )}
                </Button>
              </Link>
              
              {/* User Account */}
              {isAuthenticated && customer ? (
                <div className="flex items-center gap-2">
                  <Link href="/area-clienti" data-testid="link-customer-name">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-uo-gold/30 text-uo-dark hover:bg-uo-gold/10"
                      data-testid="button-customer-name"
                    >
                      <User className="h-4 w-4 mr-1" />
                      {customer.firstName} {customer.lastName}
                    </Button>
                  </Link>
                  <Button 
                    variant="default" 
                    size="sm"
                    className="bg-uo-gold text-uo-dark hover:bg-uo-gold/90"
                    onClick={() => {
                      logout();
                      setLocation('/');
                    }}
                    data-testid="button-logout"
                  >
                    <LogOut className="h-4 w-4 mr-1" />
                    Logout
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Link href="/accedi" data-testid="link-login">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-uo-gold/30 text-uo-dark hover:bg-uo-gold/10"
                      data-testid="button-login"
                    >
                      <User className="h-4 w-4 mr-1" />
                      Accedi
                    </Button>
                  </Link>
                  <Link href="/registrati" data-testid="link-register">
                    <Button 
                      variant="default" 
                      size="sm"
                      className="bg-uo-gold text-uo-dark hover:bg-uo-gold/90"
                      data-testid="button-register"
                    >
                      Registrati
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Bar - Only 4 main sections, centered */}
      <div className={`bg-[hsl(var(--header-bg))] border-b border-uo-gold/30 z-50 ${
        isScrolled ? 'fixed top-0 left-0 right-0 shadow-lg' : 'sticky top-0'
      }`}>
        <div className="container mx-auto max-w-7xl px-4">
          {/* Non-scrolled: Simple centered nav */}
          {!isScrolled && (
            <div className="hidden md:flex items-center justify-center py-3">
              <nav className="flex items-center gap-2" aria-label="Navigazione principale">
                {/* Home Icon */}
                <Link href="/" data-testid="link-home-nav" aria-label="Home">
                  <Button 
                    variant={location === "/" ? "default" : "ghost"}
                    size="icon"
                    className={`${
                      location === "/" 
                        ? 'bg-uo-gold text-uo-dark' 
                        : 'text-white hover:text-uo-gold hover:bg-uo-gold/10'
                    }`}
                    data-testid="button-home-nav"
                    aria-label="Home"
                  >
                    <Home className="h-4 w-4" />
                  </Button>
                </Link>
                
                {/* Vertical gold divider */}
                <div className="h-6 w-px bg-primary/50 mx-1" />
                
                {menuItems.map((item) => {
                  const isActive = isMenuItemActive(item);
                  return (
                    <Link 
                      key={item.href} 
                      href={item.href} 
                      data-testid={`link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Button 
                        variant={isActive ? "default" : "ghost"}
                        size="sm"
                        className={`text-xs font-medium uppercase tracking-wide ${
                          isActive 
                            ? 'bg-uo-gold text-uo-dark' 
                            : 'text-white hover:text-uo-gold hover:bg-uo-gold/10'
                        }`}
                        data-testid={`button-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {item.label}
                      </Button>
                    </Link>
                  );
                })}
                
                {/* Area Clienti - visible when authenticated */}
                {isAuthenticated && (
                  <>
                    <div className="h-6 w-px bg-primary/50 mx-1" />
                    <Link 
                      href="/area-clienti" 
                      data-testid="link-area-clienti-nav"
                      onMouseEnter={prefetchHandlers.areaClienti.onMouseEnter}
                      onMouseLeave={prefetchHandlers.areaClienti.onMouseLeave}
                    >
                      <Button 
                        variant={location.startsWith('/area-clienti') ? "default" : "ghost"}
                        size="sm"
                        className={`text-xs font-medium uppercase tracking-wide ${
                          location.startsWith('/area-clienti') 
                            ? 'bg-uo-gold text-uo-dark' 
                            : 'text-white hover:text-uo-gold hover:bg-uo-gold/10'
                        }`}
                        data-testid="button-area-clienti-nav"
                      >
                        AREA CLIENTI
                      </Button>
                    </Link>
                  </>
                )}
              </nav>
            </div>
          )}
          
          {/* Scrolled: Grid layout for perfect centering */}
          {isScrolled && (
            <div className="hidden md:grid grid-cols-[auto_1fr_auto] items-center gap-4 py-2">
              {/* Logo - Left */}
              <Link href="/" data-testid="link-home-scrolled">
                <div className="cursor-pointer group">
                  <img 
                    src={logoMonochrome} 
                    alt="UNIVERSO ORO Logo" 
                    className="h-[40px] w-auto"
                    width={161}
                    height={80}
                  />
                </div>
              </Link>
              
              {/* Navigation Menu - True center */}
              <nav className="flex items-center justify-center gap-2" aria-label="Navigazione principale">
                {/* Home Icon */}
                <Link href="/" data-testid="link-home-nav-scrolled" aria-label="Home">
                  <Button 
                    variant={location === "/" ? "default" : "ghost"}
                    size="icon"
                    className={`${
                      location === "/" 
                        ? 'bg-uo-gold text-uo-dark' 
                        : 'text-white hover:text-uo-gold hover:bg-uo-gold/10'
                    }`}
                    data-testid="button-home-nav-scrolled"
                    aria-label="Home"
                  >
                    <Home className="h-4 w-4" />
                  </Button>
                </Link>
                
                {/* Vertical gold divider */}
                <div className="h-6 w-px bg-primary/50 mx-1" />
                
                {menuItems.map((item) => {
                  const isActive = isMenuItemActive(item);
                  return (
                    <Link 
                      key={item.href} 
                      href={item.href} 
                      data-testid={`link-${item.label.toLowerCase().replace(/\s+/g, '-')}-scrolled`}
                    >
                      <Button 
                        variant={isActive ? "default" : "ghost"}
                        size="sm"
                        className={`text-xs font-medium uppercase tracking-wide ${
                          isActive 
                            ? 'bg-uo-gold text-uo-dark' 
                            : 'text-white hover:text-uo-gold hover:bg-uo-gold/10'
                        }`}
                        data-testid={`button-${item.label.toLowerCase().replace(/\s+/g, '-')}-scrolled`}
                      >
                        {item.label}
                      </Button>
                    </Link>
                  );
                })}
                
                {/* Area Clienti - visible when authenticated */}
                {isAuthenticated && (
                  <>
                    <div className="h-6 w-px bg-primary/50 mx-1" />
                    <Link 
                      href="/area-clienti" 
                      data-testid="link-area-clienti-nav-scrolled"
                      onMouseEnter={prefetchHandlers.areaClienti.onMouseEnter}
                      onMouseLeave={prefetchHandlers.areaClienti.onMouseLeave}
                    >
                      <Button 
                        variant={location.startsWith('/area-clienti') ? "default" : "ghost"}
                        size="sm"
                        className={`text-xs font-medium uppercase tracking-wide ${
                          location.startsWith('/area-clienti') 
                            ? 'bg-uo-gold text-uo-dark' 
                            : 'text-white hover:text-uo-gold hover:bg-uo-gold/10'
                        }`}
                        data-testid="button-area-clienti-nav-scrolled"
                      >
                        AREA CLIENTI
                      </Button>
                    </Link>
                  </>
                )}
              </nav>

              {/* Actions - Right */}
              <div className="flex items-center gap-2">
                <div className="w-44">
                  <SearchBar />
                </div>
                <Link href="/carrello" data-testid="link-cart-scrolled" aria-label="Carrello">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="relative text-white hover:text-uo-gold"
                    data-testid="button-cart-scrolled"
                    aria-label="Carrello"
                  >
                    <ShoppingCart className="h-5 w-5" />
                    {cartItemCount > 0 && (
                      <Badge 
                        className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-uo-gold text-uo-dark"
                        data-testid="cart-count-scrolled"
                      >
                        {cartItemCount > 99 ? '99+' : cartItemCount}
                      </Badge>
                    )}
                  </Button>
                </Link>
                {isAuthenticated && customer ? (
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="text-white hover:text-uo-gold"
                    onClick={() => {
                      logout();
                      setLocation('/');
                    }}
                    data-testid="button-logout-scrolled"
                    aria-label="Logout"
                  >
                    <LogOut className="h-5 w-5" />
                  </Button>
                ) : (
                  <Link href="/accedi" data-testid="link-login-scrolled" aria-label="Accedi">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="text-white hover:text-uo-gold"
                      data-testid="button-login-scrolled"
                      aria-label="Accedi"
                    >
                      <User className="h-5 w-5" />
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Mobile Menu */}
          <div className="md:hidden flex items-center justify-between py-2">
            <Link href="/" data-testid="link-home-mobile">
              <div className="flex items-center gap-1 p-1">
                <img src={logoMonochrome} alt="UNIVERSO ORO" className="h-10 w-auto" width={161} height={80} />
              </div>
            </Link>
            <MobileNav goldPrice={goldPrice ?? null} silverPrice={silverPrice ?? null} quotationStatus={quotations?.status} />
          </div>
        </div>
      </div>
    </header>
  );
}
